import { PathLocationStrategy, PlatformLocation } from '@angular/common';
import { inject, Injectable } from '@angular/core';

export interface VirtualState {
  virtualPathStack: string[];
  state: unknown;
}

function isVirtualState(state: unknown): state is VirtualState {
  return !!state && typeof state === 'object' && 'virtualPathStack' in state && 'state' in state;
}

/**
 * A locationStrategy that maintains a stack of virtual paths in the History API’s state.
 */
@Injectable({ providedIn: null })
export class StackLocationStrategy extends PathLocationStrategy {
  private readonly location = inject(PlatformLocation);

  override getState(): unknown {
    return this.getVirtualState().state;
  }

  override pushState(state: unknown, title: string, url: string, queryParams: string): void {
    const virtualPathStack = this.getVirtualPathStack().concat(url);
    const virtualState: VirtualState = { virtualPathStack, state };
    super.pushState(virtualState, title, url, queryParams);
  }

  override replaceState(state: unknown, title: string, url: string, queryParams: string): void {
    const virtualPathStack = this.getVirtualPathStack().slice();
    virtualPathStack.splice(-1, 1, url);
    const virtualState: VirtualState = { virtualPathStack, state };
    super.replaceState(virtualState, title, url, queryParams);
  }

  getVirtualPathStack() {
    return this.getVirtualState().virtualPathStack;
  }

  private getVirtualState(): VirtualState {
    const state = this.location.getState();
    return isVirtualState(state) ? state : { virtualPathStack: [], state: undefined };
  }
}
